/**
 * Display template renderer.
 *
 * R-146 — tenant-level batch/document/page templates.
 * R-167 — extends additively with composite dates, counters, sourceCode,
 *         deviceId, field references, and position tokens with inline padding.
 *
 * Tenants configure free-form templates (e.g. `{shortName}-{YYYY}{MM}{DD}-{counter:batchSeq}`).
 * displayId remains the canonical machine identifier; this helper is presentation-only.
 *
 * Falls back to record.displayId when:
 *   - template is null / undefined / empty / only whitespace
 *   - rendering throws for any reason
 *   - the rendered output is empty
 *   - the template contained tokens AND none resolved (every token was unknown)
 */
// R-167: token grammar widens with optional `:arg` suffix.
const TOKEN_RE = /\{([a-zA-Z][a-zA-Z0-9_]*)(?::([^}]+))?\}/g;
function pad2(n) {
    return n < 10 ? `0${n}` : String(n);
}
function toDate(value) {
    if (!value)
        return null;
    if (value instanceof Date)
        return Number.isNaN(value.getTime()) ? null : value;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
}
function trailingDigits(value) {
    const m = value.match(/(\d+)\s*$/);
    return m ? m[1] : "";
}
function padWidth(value, width) {
    return String(value).padStart(width, "0");
}
function resolveToken(token, arg, record, createdAt) {
    switch (token) {
        // R-146 tokens
        case "displayId":
            return record.displayId;
        case "shortName":
            return record.jobType?.shortName ?? "";
        case "YYYY":
            return createdAt ? String(createdAt.getUTCFullYear()) : undefined;
        case "MM":
            return createdAt ? pad2(createdAt.getUTCMonth() + 1) : undefined;
        case "DD":
            return createdAt ? pad2(createdAt.getUTCDate()) : undefined;
        case "seq": {
            if (record.seq !== undefined && record.seq !== null && record.seq !== "") {
                return String(record.seq);
            }
            const fromId = trailingDigits(record.displayId ?? "");
            return fromId || undefined;
        }
        // R-167 — composite date tokens
        case "YYYYMMDD":
            return createdAt
                ? `${createdAt.getUTCFullYear()}${pad2(createdAt.getUTCMonth() + 1)}${pad2(createdAt.getUTCDate())}`
                : undefined;
        case "YYYYMM":
            return createdAt
                ? `${createdAt.getUTCFullYear()}${pad2(createdAt.getUTCMonth() + 1)}`
                : undefined;
        case "YYMMDD": {
            if (!createdAt)
                return undefined;
            const yy = String(createdAt.getUTCFullYear()).slice(-2);
            return `${yy}${pad2(createdAt.getUTCMonth() + 1)}${pad2(createdAt.getUTCDate())}`;
        }
        // R-167 — counter
        case "counter": {
            if (!arg)
                return undefined;
            const v = record.counterValues?.[arg];
            if (v === undefined || v === null)
                return undefined;
            const pw = record.counterPadding?.[arg] ?? 0;
            return pw > 0 ? padWidth(v, pw) : String(v);
        }
        // R-167 — source code / device id
        case "sourceCode":
            return record.sourceCode ?? "";
        case "deviceId":
            return record.deviceId ?? "";
        // R-167 — field reference
        case "field": {
            if (!arg)
                return undefined;
            const v = record.fieldValues?.[arg];
            return v == null ? "" : String(v);
        }
        // R-167 — position tokens (optional inline padding)
        case "docPosition": {
            if (record.docPosition === undefined || record.docPosition === null)
                return undefined;
            const w = arg ? Number.parseInt(arg, 10) : 0;
            return w > 0 ? padWidth(record.docPosition, w) : String(record.docPosition);
        }
        case "pagePosition": {
            if (record.pagePosition === undefined || record.pagePosition === null)
                return undefined;
            const w = arg ? Number.parseInt(arg, 10) : 0;
            return w > 0 ? padWidth(record.pagePosition, w) : String(record.pagePosition);
        }
        default:
            return undefined;
    }
}
export function renderDisplayTemplate(template, record) {
    const fallback = record.displayId ?? "";
    if (template == null)
        return fallback;
    const trimmed = template.trim();
    if (trimmed === "")
        return fallback;
    try {
        const createdAt = toDate(record.createdAt ?? null);
        let anyTokenResolved = false;
        const rendered = template.replace(TOKEN_RE, (match, tokenName, arg) => {
            const value = resolveToken(tokenName, arg, record, createdAt);
            if (value === undefined) {
                // Token didn't resolve — leave literal so the tenant sees what's wrong.
                return match;
            }
            anyTokenResolved = true;
            return value;
        });
        const hadTokens = TOKEN_RE.test(template);
        TOKEN_RE.lastIndex = 0;
        if (hadTokens && !anyTokenResolved)
            return fallback;
        const out = rendered.trim();
        return out === "" ? fallback : rendered;
    }
    catch {
        return fallback;
    }
}
export function preferredDisplayLabel(record) {
    return record.customDisplayLabel ?? record.displayId;
}
export function preferredBatchLabel(record) {
    return record.customDisplayLabel ?? record.displayId ?? record.name;
}
const ALL_TOKEN_NAMES = new Set([
    "displayId",
    "shortName",
    "YYYY",
    "MM",
    "DD",
    "seq",
    "YYYYMMDD",
    "YYYYMM",
    "YYMMDD",
    "counter",
    "sourceCode",
    "deviceId",
    "field",
    "docPosition",
    "pagePosition",
]);
export function validateTemplate(template, ctx) {
    const issues = [];
    if (!template)
        return issues;
    // Reset regex state defensively
    TOKEN_RE.lastIndex = 0;
    let m;
    while ((m = TOKEN_RE.exec(template)) !== null) {
        const [full, name, arg] = m;
        if (!ALL_TOKEN_NAMES.has(name)) {
            issues.push({
                kind: "unknown-token",
                token: full,
                message: `Unknown token: ${full}`,
            });
            continue;
        }
        if (name === "counter") {
            if (!arg) {
                issues.push({ kind: "malformed", token: full, message: `Missing counter name in ${full}` });
            }
            else if (!ctx.knownCounterNames.has(arg)) {
                issues.push({
                    kind: "unknown-counter",
                    token: full,
                    message: `No counter named '${arg}' is configured for this job type or tenant.`,
                });
            }
        }
        if (name === "field") {
            if (!arg) {
                issues.push({ kind: "malformed", token: full, message: `Missing field name in ${full}` });
            }
            else if (!ctx.knownFieldInternalNames.has(arg)) {
                issues.push({
                    kind: "unknown-field",
                    token: full,
                    message: `No field named '${arg}' is defined for this job type.`,
                });
            }
        }
    }
    TOKEN_RE.lastIndex = 0;
    return issues;
}
// ─── R-167 — extractCounterNames / extractFieldRefs ────────────────────
//
// Tiny pure helpers for parsing a template string without instantiating the
// full renderer. WU-4 callers (write-path service) use these to figure out
// which counters to allocate and which document fields to read up-front.
function extractNamedRefs(template, targetName) {
    const out = new Set();
    TOKEN_RE.lastIndex = 0;
    let m;
    while ((m = TOKEN_RE.exec(template)) !== null) {
        const [, name, arg] = m;
        if (name === targetName && arg)
            out.add(arg);
    }
    TOKEN_RE.lastIndex = 0;
    return [...out];
}
export function extractCounterNames(template) {
    return extractNamedRefs(template, "counter");
}
export function extractFieldRefs(template) {
    return extractNamedRefs(template, "field");
}
export const TOKEN_CATALOG = [
    // Identity
    { token: "{displayId}", description: "Capture's internal structured identifier", example: "NOW_20260527_0001", group: "identity" },
    { token: "{shortName}", description: "JobType short name", example: "DE1545", group: "identity" },
    { token: "{seq}", description: "Sequence (fallback: trailing digits of displayId)", example: "42", group: "identity" },
    // Date
    { token: "{YYYY}", description: "4-digit year", example: "2026", group: "date" },
    { token: "{MM}", description: "2-digit month", example: "05", group: "date" },
    { token: "{DD}", description: "2-digit day", example: "27", group: "date" },
    { token: "{YYYYMMDD}", description: "Full date (no separator)", example: "20260527", group: "date" },
    { token: "{YYYYMM}", description: "Year + month", example: "202605", group: "date" },
    { token: "{YYMMDD}", description: "Short date", example: "260527", group: "date" },
    // Counter
    { token: "{counter:NAME}", description: "Named counter (configure on /admin/counters)", example: "2003", group: "counter" },
    // Source
    { token: "{sourceCode}", description: "Tenant channel→code mapping (configure on Tenant Settings)", example: "SCN", group: "source" },
    // 2026-08-07 — Web TWAIN descoped for v1; scanner traffic arrives via the
    // Pull Agent / hot folders, which report the device name this code keys on.
    { token: "{deviceId}", description: "Scanner device code (per-device override; scanner-channel batches only)", example: "SS01", group: "source" },
    // Field
    { token: "{field:INTERNAL_NAME}", description: "Designated field value on batch / document", example: "ABC-99", group: "field" },
    // Position
    { token: "{docPosition}", description: "Document position within batch", example: "3", group: "position" },
    { token: "{docPosition:02}", description: "Document position, zero-padded to 2 digits", example: "03", group: "position" },
    { token: "{pagePosition}", description: "Page position within document", example: "12", group: "position" },
    { token: "{pagePosition:04}", description: "Page position, zero-padded to 4 digits", example: "0012", group: "position" },
];
//# sourceMappingURL=display-templates.js.map